import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@mui/styles'
import { IconButton, Popper, Grow, Zoom, Slide, Box, SvgIcon, OutlinedInput } from '@mui/material'
import { removeClip, unselectChange, updateSelectedChange, allPositionsLower, allPositionsHigher } from './editorSlice'
import { transformStepToMeter, getChangeLength, getNumeral, getNumeralInMajor, getRealRoot, getAvailableFills, checkFill, getPitchJSX, getTypeJSX, getNumeralJSX } from './editorUtils'
import { PITCHES, NUMERALS, MAJOR_TYPES_PRESET, MAJOR_NUMERALS_PRESET, MAJOR_EXTRA_NUMERALS_PRESET, NUMERAL_TYPES, NUM_STEPS_PER_BEAT } from './editorConstants'
import StyledPopup from '../../components/StyledPopup'
import StyledDivider from '../../components/StyledDivider'
import StyledButton from '../../components/StyledButton'
import StyledTypography from '../../components/StyledTypography'
import StyledTooltip from '../../components/StyledTooltip'
import {StyledMenu, StyledMenuItem} from '../../components/StyledSelect'
import { StyledList, StyledListItem, StyledListItemText } from  '../../components/StyledList'
import StyledSlider from '../../components/StyledSlider'
import PitchSelect from './PitchSelect'
import Confirmation from '../../components/Confirmation'
import './ClipAttributes.css'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import {ReactComponent as FillSelectedIcon} from './icons/fill-selected.svg'
import {ReactComponent as FillUnSelectedIcon} from './icons/fill-unselected.svg'
import {ReactComponent as BackIcon} from './icons/back.svg'
import {ReactComponent as GrooveLoopIcon} from './icons/groove-loop.svg'
import {ReactComponent as GrooveOneShotIcon} from './icons/groove-one-shot.svg'
import {ReactComponent as LickLoopIcon} from './icons/lick-loop.svg'
import {ReactComponent as LickOneShotIcon} from './icons/lick-one-shot.svg'
import useWindowSize from '../../hooks/window-size';

const useStyles = makeStyles(theme => ({
    popper: {
      zIndex: theme.zIndex.modal + 1
    },
    input: {
        color: theme.palette.primary.main,
        padding: 2,
        textAlign: 'center',
        fontSize: '12px'
    }
}));

export default function ClipAttributes(props) {
    const classes = useStyles();
    const {showProgression, wavesurfer, packId, preview} = props;
    const status = useSelector(state => state.editor.status)
    const key = useSelector(state => state.editor.key);
    const mode = useSelector(state => state.editor.mode);
    const clips = useSelector(state => state.editor.clips);
    const config = useSelector(state => state.editor.config);
    const selectedClipIndex = useSelector(state => state.editor.selectedClipIndex);
    const selectedChangeIndex = useSelector(state => state.editor.selectedChangeIndex);
    const [chordAnchorEl, setChordAnchorEl] = useState(null);
    const [positionAnchorEl, setPositionAnchorEl] = useState(null);
    const [fillAnchorEl, setFillAnchorEl] = useState(null);
    const [moreAnchorEl, setMoreAnchorEl] = useState(null);
    const [typeSlotsIn, setTypeSlotsIn] = useState(true);
    const [isChordPresetPage, setChordPresetPage] = useState(true);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [isPlaying, setPlaying] = useState(false);
    const [showBpm, setShowBpm] = useState(false);
    const [gainText, setGainText] = useState('');
    const { height } = useWindowSize();
        
    const dispatch = useDispatch();
    
    useEffect(() => {
        if(selectedClipIndex === -1 || selectedChangeIndex === -1 || showProgression || status !== 'editing')
        {
            setChordAnchorEl(null);
            setPositionAnchorEl(null);
            setFillAnchorEl(null);
            setMoreAnchorEl(null);
            if (showProgression)
                dispatch(unselectChange());
        }
        else
        {
            const change = clips[selectedClipIndex].changes[selectedChangeIndex];
            setGainText(change.gain !== undefined ? change.gain.toString() : '0');
            playChangePreview(change);
            if(clips[selectedClipIndex].type !== 'groove')
            {
                setChordAnchorEl(null);
                setFillAnchorEl(null);
            }
        }
    }, [selectedClipIndex, selectedChangeIndex, showProgression, status]); 

    function playChangePreview(changeToPlay)
    {
        if (!preview)
            return;
        const clip = clips[selectedClipIndex];
        const group = config.groups.find(group => group.id === clip.id && group.type === clip.type);
        let previewUrl = preview + (clip.type === 'groove' && changeToPlay.fill !== null ? 'fills/' + changeToPlay.fill 
        : (clip.type + 's/' + clip.id)) + '/';
        if (clip.type === 'groove')
        {
            if (!clip.numeral && changeToPlay.fill === null)
                previewUrl += changeToPlay.root.replace('F#', 'Gb') + changeToPlay.type + '-' + changeToPlay.position;
            else
            {
                const root = getRealRoot(group, changeToPlay.root);
                const numeralGroup = changeToPlay.fill !== null ? config.groups.find(group => group.id === changeToPlay.fill && group.type === 'fill') : group;
                const sample = numeralGroup.samples.find(sample => sample.pitch === root.pitch.replace('F#', 'Gb') && sample.position === changeToPlay.position
                        && sample.numerals.includes(changeToPlay.numeral));
                previewUrl += changeToPlay.root.replace('F#', 'Gb') + '-' + sample.numerals.join(',') + '-' + changeToPlay.position;
                if (changeToPlay.fill !== null)
                    previewUrl += '-' + group.bpm.toString()
            }
                    
                    
            if (changeToPlay.fill === null && clip.changes.length > 1)
                previewUrl += '-' + selectedChangeIndex.toString();
        }
        else if(clip.type === 'lick')
            previewUrl += PITCHES[(PITCHES.indexOf(key) + (mode === 'major' ? 0 : 3)) % PITCHES.length].replace('F#', 'Gb') + '-' + changeToPlay.position;
        previewUrl += '.mp3';
        if (isPlaying || wavesurfer.isPlaying())
        {
            wavesurfer.stop();
            setPlaying(false);
        }
        wavesurfer.unAll();
        wavesurfer.load(previewUrl);
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '') || /iPad|iPhone|iPod/i.test(navigator.userAgent || '');
        wavesurfer.on("ready", () => {
            setPlaying(true);
            if (!isSafari && changeToPlay.gain !== undefined && changeToPlay.gain != 0)
                wavesurfer.getFilters()[0].gain.value = Math.pow(10, changeToPlay.gain / 20);
            const promise = wavesurfer.play();
            let changeEnd = clip.beats === 0 ? clip.sampleEnd : (selectedChangeIndex === clip.changes.length - 1 ? clip.beats * NUM_STEPS_PER_BEAT : clip.changes[selectedChangeIndex + 1].start);
            if (changeEnd > clip.sampleEnd)
                changeEnd = clip.sampleEnd;
            changeEnd -= changeToPlay.start;
            const changeStart = clip.sampleStart > changeToPlay.start ? clip.sampleStart - changeToPlay.start : 0;
            const bpm = group.bpm === 0 ? 60 : group.bpm;
            if (changeStart > 0)
                wavesurfer.skip(changeStart / NUM_STEPS_PER_BEAT * 60 / bpm + 0.023); //23ms head for mp3 
            wavesurfer.setPlayEnd(changeEnd / NUM_STEPS_PER_BEAT * 60 / bpm);
            if (promise !== undefined) {
                promise.catch(error => {
                    setPlaying(false);
                })
            }
        });
        wavesurfer.on("pause", () => {
            setPlaying(false);
            if (!isSafari)
                wavesurfer.getFilters()[0].gain.value = 1.0;
        })
        wavesurfer.on("finish", () => {
            setPlaying(false);
            if (!isSafari)
                wavesurfer.getFilters()[0].gain.value = 1.0;
        });
    }

    if (selectedClipIndex !== -1 && selectedChangeIndex !== -1) {
        const clip = clips[selectedClipIndex];
        const group = config.groups.find(group => group.id === clip.id && group.type === clip.type);
        const change = clip.changes[selectedChangeIndex];
        const numerals_in_major = Object.keys(MAJOR_TYPES_PRESET);
        const extra_numerals_in_major = Object.keys(MAJOR_EXTRA_NUMERALS_PRESET);
        const change_numeral_in_major = group.type === 'groove' ? getNumeralInMajor(key, mode, change.root) : null;
        const isPreset = numerals_in_major.includes(change_numeral_in_major);
        const hasFill = group.type === 'groove' && group.changes[selectedChangeIndex].fills !== null;
        const [fills, fillNumeral] = hasFill ? getAvailableFills(config, group, clip, change, selectedChangeIndex, change_numeral_in_major) : [[], null];
        let matchTypes = [];

        function NumeralChordSlot(numeral_in_major, index, extra) {
            const numeral = NUMERALS[(NUMERALS.indexOf(numeral_in_major) - NUMERALS.indexOf(mode === 'major' ? 'I' : 'VI') + NUMERALS.length) % NUMERALS.length];
            const root = PITCHES[(PITCHES.indexOf(key) + NUMERALS.indexOf(numeral)) % PITCHES.length];
            const match = root === change.root;
            let numerals, types;
            if (clip.numeral)
            {
                numerals = (extra ? MAJOR_EXTRA_NUMERALS_PRESET : MAJOR_NUMERALS_PRESET)[numeral_in_major].filter(numeral => group.numerals.includes(numeral));
                types = numerals.map(numeral => NUMERAL_TYPES[numeral]);
            }
            else
                types = group.types !== null ? MAJOR_TYPES_PRESET[numeral_in_major].filter(type => group.types.includes(type)) : [];
            const type = types.length > 0 ? match ? clip.numeral ? NUMERAL_TYPES[change.numeral]: change.type : types[0] : '';
            if (match && (isChordPresetPage ^ extra))
                matchTypes = types;
            function handleClick() {
                //if (root !== change.root)
                //{
                    const newChange = { ...change };
                    newChange.root = root;
                    if (clip.numeral)
                        newChange.numeral = match ? change.numeral : numerals[0];
                    else
                        newChange.type = type;
                    checkFill(config, clip, newChange, selectedChangeIndex, key, mode);
                    playChangePreview(newChange);
                    dispatch(updateSelectedChange(newChange));
                    setTypeSlotsIn(false);
                    setTimeout(() => {
                        setTypeSlotsIn(true);
                    }, 50);
                //}
            }

            return (
                    <div key={index} className={match ? 'chord-slot selected' : 'chord-slot'} onClick={() => handleClick()}>
                        <div style={{fontWeight: 700}}>{getNumeralJSX(numeral, type)}</div>
                        <div>{getPitchJSX(root)}{getTypeJSX(type)}</div> 
                        {match && type.length > 0 && 
                        <div className='chord-slot-connector'>
                            <div className='chord-slot-connector-left' />
                            <div className='chord-slot-connector-middle' />
                            <div className='chord-slot-connector-right' />
                        </div>}  
                    </div>    
            );
        }

        function CustomChordSlot() {
            return (
                <div className={isPreset ? 'chord-slot' : 'chord-slot selected'} onClick={() => setChordPresetPage(false)}>
                    <div style={{fontWeight: 700}}>Custom</div>
                    <div>{!isPreset && <React.Fragment>{getPitchJSX(change.root)}{getTypeJSX(change.type)}</React.Fragment>}</div>
                </div>
            );
        }

        function ExtraNumeralChordSlot() {
            return (
                <div className={isPreset ? 'chord-slot' : 'chord-slot selected'} onClick={() => setChordPresetPage(false)} >
                    <div style={{fontWeight: 700}}>Extra</div>
                    <div>{!isPreset && <React.Fragment>{getPitchJSX(change.root)}{getTypeJSX(NUMERAL_TYPES[change.numeral])}</React.Fragment>}</div>
                </div>
            );
        }

        function TypeSlot(type, index) {
            function handleClick() {
                const newChange = { ...change };
                if (clip.numeral)
                    newChange.numeral = (isChordPresetPage ? MAJOR_NUMERALS_PRESET : MAJOR_EXTRA_NUMERALS_PRESET)[change_numeral_in_major].filter(numeral => group.numerals.includes(numeral))[index];
                else
                    newChange.type = type;
                //if ((clip.numeral && newChange.numeral !== change.numeral) || (!clip.numeral && newChange.type !== change.type))
                //{
                    checkFill(config, clip, newChange, selectedChangeIndex, key, mode);
                    playChangePreview(newChange);
                    dispatch(updateSelectedChange(newChange));
                //}
            }
            return <Zoom in={typeSlotsIn} timeout={{exit:0, enter:200}} key={index}><div key={index} className={(clip.numeral ? NUMERAL_TYPES[change.numeral] : change.type) === type ? 'type-preset-slot selected' : 'type-preset-slot'}
                     onClick={() => handleClick()}>{getPitchJSX(change.root)}{getTypeJSX(type)}</div></Zoom>;
        }

        function handleClickAway()
        {
            setChordAnchorEl(null);
            setPositionAnchorEl(null);
            setMoreAnchorEl(null);
        }

        function handleChordClick(e)
        {
            e.stopPropagation(); 
            if (!Boolean(chordAnchorEl))
                setChordPresetPage(true);
            setChordAnchorEl(Boolean(chordAnchorEl) ? null : e.currentTarget);
            setPositionAnchorEl(null);
            setMoreAnchorEl(null);
        }

        function handlePositionClick(e)
        {
            e.stopPropagation(); 
            setPositionAnchorEl(Boolean(positionAnchorEl) ? null : e.currentTarget);
            setChordAnchorEl(null);   
            setMoreAnchorEl(null);
        }

        function handleMoreClick(e)
        {
            e.stopPropagation();
            setMoreAnchorEl(Boolean(moreAnchorEl) ? null : e.currentTarget);
            setChordAnchorEl(null);
            setPositionAnchorEl(null);
        }

        function handleRootChange(root) {
            //if (change.root !== root)
            //{
                const newChange = { ...change };
                newChange.root = root;
                checkFill(config, clip, newChange, selectedChangeIndex, key, mode);
                playChangePreview(newChange);
                dispatch(updateSelectedChange(newChange));
            //}
        }

        function handleTypeChange(type) {
            //if (change.type !== type)
            //{
                const newChange = { ...change };
                newChange.type = type;
                checkFill(config, clip, newChange, selectedChangeIndex, key, mode);
                playChangePreview(newChange);
                dispatch(updateSelectedChange(newChange));
            //}
        }

        function handlePositionChange(position) {
            //if (change.position !== position)
            //{
                const newChange = { ...change };
                newChange.position = position;
                checkFill(config, clip, newChange, selectedChangeIndex, key, mode);
                playChangePreview(newChange);
                dispatch(updateSelectedChange(newChange));
            //}
        }

        function handleFillChange(fill) {
            const newChange = { ...change };
            //if ((newChange.fill === null && fill !== null) || (newChange.fill !== null && newChange.fill !== fill))
            //{
                if (fill !== null)
                {
                    newChange.fill = fill;
                    newChange.numeral = fillNumeral;
                }
                else
                    newChange.fill = null;
                playChangePreview(newChange);
                dispatch(updateSelectedChange(newChange));
            //}
        }

        return (
            <div id='clip-attributes' className='clip-attributes' onClick={() => handleClickAway()}>
                <div className='clip-name'>
                    <StyledTooltip open={group.length > 0 && showBpm} placement='top' arrow title={'Original BPM ' + group.bpm.toString()}>
                        <SvgIcon style={{marginTop: '4px', marginRight: '10px'}} onMouseEnter={() => setShowBpm(true)} onMouseLeave={() => setShowBpm(false)} 
                            component={clip.type === 'groove' ? (clip.beats === 0 ? GrooveOneShotIcon : GrooveLoopIcon) : (clip.beats === 0 ? LickOneShotIcon : LickLoopIcon)}/>
                    </StyledTooltip>
                    <StyledTypography style={{marginRight: 0,  whiteSpace: 'nowrap'}}>{group.name !== undefined ? group.name : group.id}</StyledTypography>
                    <IconButton disableRipple style={{color: '#5A5A5A'}} onClick={() => setDeleteOpen(true)}>
                        <DeleteOutlineIcon />
                    </IconButton>
                </div>
                <div className='change-attributes'>
                    <IconButton disableRipple style={{color: isPlaying ? '#FF6500' : '#5A5A5A'}} onClick={(e) => {e.stopPropagation(); isPlaying ? wavesurfer.stop() : playChangePreview(change);}}>
                        <HeadphonesIcon />
                    </IconButton>
                    <StyledTypography>{'Start ' + transformStepToMeter(clip.loopStart + change.start, 1)}</StyledTypography>
                    <StyledTypography style={{marginRight: '20px'}}>{'Length ' + transformStepToMeter(getChangeLength(clip, selectedChangeIndex))}</StyledTypography>
                    {clip.type === 'groove' &&
                    <React.Fragment>
                    <StyledTypography>Chord</StyledTypography>
                        <StyledPopup style={{ marginRight: '10px', minWidth: '165px' }} value={<React.Fragment>{getNumeralJSX(getNumeral(key, change.root))}{' '}{getPitchJSX(change.root)}{getTypeJSX(clip.numeral ? NUMERAL_TYPES[change.numeral] : change.type)}</React.Fragment>} 
                            open={Boolean(chordAnchorEl)}
                            handleClick={e => handleChordClick(e)} />
                        <Popper
                            id={Boolean(chordAnchorEl) ? 'chord-popper' : undefined}
                            className={classes.popper}
                            open={Boolean(chordAnchorEl)}
                            anchorEl={chordAnchorEl}
                            transition
                            placement='bottom'
                            modifiers={[{name: 'offset', options: {offset: [0, height < 860 ? height - 855 : 5]}}]}
                        >
                            {({ TransitionProps }) => (
                            <Grow {...TransitionProps} timeout={showProgression ? 0 : 350}>
                                <div className={group.types !== null ? 'chord-popup' : 'chord-popup simple'} onClick={e => e.stopPropagation()}>
                                    <div className='chord-header'>
                                        {/* <ChordIcon style={{transform: 'translateX(-9px)', width: '90px'}} /> */}
                                        <div style={{fontSize: '18px', pointerEvents: 'none', color:'inherit)'}}>{isChordPresetPage ? ' SELECT CHORD' : (clip.numeral ? 'EXTRA CHORD' : 'CUSTOM CHORD')}</div>
                                        {!isChordPresetPage && <StyledButton style={{position: 'absolute', right: 'calc(100% - 90px)'}} startIcon={<BackIcon />} onClick={() => setChordPresetPage(true)}>Back</StyledButton>}
                                    </div>
                                    <div className='chord-line' />
                                    <div className='chord-body'>
                                        {
                                            isChordPresetPage &&
                                            <React.Fragment>
                                                <div className='chord-slots'>
                                                    {(mode === 'minor' ? numerals_in_major.slice(-2).concat(numerals_in_major.slice(0, -2)) : numerals_in_major).map((numeral_in_major, index) => NumeralChordSlot(numeral_in_major, index, false))}
                                                    {clip.numeral ? <ExtraNumeralChordSlot /> : <CustomChordSlot />}
                                                </div>
                                                <div className={matchTypes.length > 0 ? 'type-preset-slots' : 'type-preset-slots empty'}>
                                                    {matchTypes.map((type, index) => TypeSlot(type, index))}
                                                </div>
                                            </React.Fragment>
                                        }
                                        <Slide direction='left' in={!isChordPresetPage} mountOnEnter unmountOnExit>
                                            <div style={{paddingTop: group.types !== null ? 0 : 22}}>
                                                { clip.numeral ? (
                                                    <React.Fragment>
                                                        <div className='chord-slots extra'>
                                                            {(mode === 'minor' ? extra_numerals_in_major.slice(-1).concat(extra_numerals_in_major.slice(0, -1)) : extra_numerals_in_major).map((numeral_in_major, index) => NumeralChordSlot(numeral_in_major, index, true))}
                                                        </div>
                                                        <div className={matchTypes.length > 0 ? 'type-preset-slots' : 'type-preset-slots empty'}>
                                                            {matchTypes.map((type, index) => TypeSlot(type, index))}
                                                        </div>
                                                    </React.Fragment>
                                                ) : (
                                                <React.Fragment>
                                                    <PitchSelect pitch={change.root} handlePitchChange={handleRootChange}/>
                                                    {group.types !== null && <div className='custom-divider' />}
                                                    <div className='type-slots'>
                                                        {group.types !== null && group.types.map((value, index) => {return <div onClick={() => handleTypeChange(value)}key={index} className={value === change.type ? 'type-slot selected': 'type-slot'}>{getTypeJSX(value)}</div>})}
                                                    </div>
                                                </React.Fragment>)}
                                            </div>
                                        </Slide>
                                    </div>
                                </div>
                            </Grow>
                            )}
                        </Popper>
                    </React.Fragment>
                    }
                    <StyledTypography>Register</StyledTypography>
                    <StyledPopup style={{ marginRight: '10px', minWidth: '75px' }} value={change.position} open={Boolean(positionAnchorEl)} handleClick={e => handlePositionClick(e)} />
                    <Popper
                        id={Boolean(chordAnchorEl) ? 'position-popper' : undefined}
                        className={classes.popper}
                        open={Boolean(positionAnchorEl)}
                        anchorEl={positionAnchorEl}
                        transition
                        placement='bottom'
                        modifiers={[{name: 'offset', options: {offset: [0, 5]}}]}
                    >
                        {({ TransitionProps }) => (
                        <Grow {...TransitionProps} timeout={showProgression ? 0 : 350}>
                            <Box minWidth={40} bgcolor={'#fff'}>
                                <StyledList dense>
                                    <StyledListItem button onClick={e => {e.stopPropagation(); dispatch(allPositionsLower());}}>
                                        <StyledListItemText>All Clips</StyledListItemText><ArrowUpwardIcon style={{fontSize: '1em', marginLeft: '2px', marginBottom: '1px'}}/>
                                    </StyledListItem>
                                    <StyledDivider />
                                    { group.positions.map((position, index) => { return (
                                        <StyledMenuItem key={index} selected={position === change.position} onClick={e => {e.stopPropagation(); handlePositionChange(position);}}>
                                            {position}
                                        </StyledMenuItem>);
                                    })}
                                    <StyledDivider />
                                    <StyledListItem button onClick={e => {e.stopPropagation(); dispatch(allPositionsHigher());}}>
                                        <StyledListItemText>All Clips</StyledListItemText><ArrowDownwardIcon style={{fontSize: '1em', marginLeft: '2px', marginBottom: '1px'}}/>
                                    </StyledListItem>
                                </StyledList>
                            </Box>
                        </Grow>
                        )}
                    </Popper>
                    {hasFill && 
                    <React.Fragment>
                        <IconButton disableRipple disabled={fills.length === 0} style={{marginRight: '6px', opacity: fills.length === 0 ? 0.4 : 1.0}} onClick={(e) => setFillAnchorEl(Boolean(fillAnchorEl) ? null : e.currentTarget)}>
                            {change.fill === null ? <FillUnSelectedIcon /> : <FillSelectedIcon />} 
                        </IconButton>
                        <StyledMenu
                        style={{transform: 'translateY(-16px)'}}
                        id="fill-menu"
                        anchorEl={fillAnchorEl}
                        open={Boolean(fillAnchorEl)}
                        onClose={() => setFillAnchorEl(null)}
                        >
                            <StyledMenuItem onClick={() => handleFillChange(null)}>RESET</StyledMenuItem>
                            <StyledDivider />
                            {fills.map((fill, index) => (<StyledMenuItem 
                            selected={change.fill !== null && fill === change.fill} 
                            key={index} 
                            tabIndex={index}
                            value={index} 
                            onClick={() => handleFillChange(fill)}>{'Fill ' + fill}</StyledMenuItem>))}
                        </StyledMenu>
                    </React.Fragment>
                    }
                    <div className='more' onClick={(e) => handleMoreClick(e)}>
                        <MoreVertIcon />
                    </div>
                    <Popper
                        id={Boolean(moreAnchorEl) ? 'more-popper' : undefined}
                        className={classes.popper}
                        open={Boolean(moreAnchorEl)}
                        anchorEl={moreAnchorEl}
                        transition
                        placement='bottom'
                        modifiers={[{name: 'offset', options: {offset: [0, 10]}}]}
                    >
                        {({ TransitionProps }) => (
                        <Grow {...TransitionProps} timeout={showProgression ? 0 : 350}>
                            <Box minWidth={150} bgcolor={'#fff'} onClick={e => e.stopPropagation()}>
                                <StyledList dense>
                                    <StyledListItem>
                                        <StyledListItemText>Gain</StyledListItemText>
                                        <StyledSlider style={{ width: '60px', marginLeft: 7}} value={change.gain !== undefined ? change.gain : 0.0} min={-3.0} max={3.0} step={0.1} 
                                        onChange={(e, value) => 
                                        {
                                            const newChange = { ...change };
                                            newChange.gain = value;
                                            setGainText(value.toString());
                                            dispatch(updateSelectedChange(newChange));
                                        }}/>
                                        <OutlinedInput color="primary" classes={{input: classes.input}} style={{width: '36px', height: '20px'}} value={gainText} 
                                        onChange={e => {
                                            const regexp = /^[-]?\d*\.?\d{0,9}$/;
                                            if(e.target.value === '' || regexp.test(e.target.value)) 
                                                setGainText(e.target.value);
                                        }} 
                                        onKeyDown={(ev) => {
                                            ev.stopPropagation();
                                            if (ev.key === 'Enter' || ev.key === 'Escape') 
                                            {
                                                if (ev.key === 'Escape')
                                                    setGainText(change.gain.toString());
                                                setTimeout(() => {
                                                    ev.target.blur();
                                                }, 50);
                                            }
                                          }}
                                        onBlur={() => {
                                            let value = gainText === '' || gainText === '-' ? 0 : (gainText.length === 1 ? parseInt(gainText) : parseFloat(parseFloat(gainText).toFixed(1)));
                                            if (value < -3.0)
                                                value = -3.0;
                                            else if (value > 3.0)
                                                value = 3.0;
                                            const newChange = { ...change };
                                            newChange.gain = value;
                                            setGainText(value.toString());
                                            dispatch(updateSelectedChange(newChange));
                                        }}/>
                                        <StyledListItemText style={{marginLeft: 2}}>dB</StyledListItemText>
                                    </StyledListItem>
                                </StyledList>
                            </Box>
                        </Grow>
                        )}
                    </Popper>
                </div>
                <Confirmation open={deleteOpen} setOpen={setDeleteOpen} onSubmit={() => dispatch(removeClip(selectedClipIndex))} content={'Remove the clip?'}/>
            </div>
        );
    }
    else
        return <div className='clip-attributes' />;
}
